import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * CLAÈ Admin — Batch Records trace.
 *
 * Reads from GET /api/admin/batch-records/search which flattens every
 * booking.items[].product_batch into a trace row. Filters by client,
 * treatment, product name, batch number and date range.
 */
export default function AdminBatchRecords() {
  const [rows, setRows] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [truncated, setTruncated] = useState(false);
  const [filters, setFilters] = useState({
    treatment_id: "", product: "", batch: "", date_from: "", date_to: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      for (const [k, v] of Object.entries(filters)) if (v) params[k] = v;
      const r = await api.get("/admin/batch-records/search", { params });
      setRows(r.data?.rows || []);
      setTruncated(!!r.data?.truncated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load batch records");
    }
    setLoading(false);
  }, [filters]);

  useEffect(() => {
    api.get("/treatments/all").then((r) => setTreatments(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const reset = () => setFilters({ treatment_id: "", product: "", batch: "", date_from: "", date_to: "" });
  const treatmentOptions = useMemo(() => treatments.map((t) => ({ id: t.id, name: t.name })), [treatments]);

  return (
    <div className="space-y-6" data-testid="admin-batch-records">
      <div>
        <h2 className="font-serif text-2xl text-stone-900">Batch Records</h2>
        <p className="text-xs text-stone-600 mt-1">
          Permanent historical record of every product &amp; batch used on each treatment. Edit batch details on the appointment itself (Dashboard → open appointment → Product &amp; Batch Record).
        </p>
      </div>

      <div className="border hairline bg-white p-4 flex items-end gap-3 flex-wrap" data-testid="batch-search-filters">
        <label className="block">
          <span className="mono-label text-[10px] text-stone-500 block mb-1">TREATMENT</span>
          <select data-testid="batch-filter-treatment" value={filters.treatment_id}
                  onChange={(e) => setFilters({ ...filters, treatment_id: e.target.value })}
                  className="border hairline px-2 py-2 text-sm focus:outline-none focus:border-stone-900 min-w-[220px]">
            <option value="">All treatments</option>
            {treatmentOptions.map((t) => (<option key={t.id} value={t.id}>{t.name}</option>))}
          </select>
        </label>
        <label className="block">
          <span className="mono-label text-[10px] text-stone-500 block mb-1">PRODUCT</span>
          <input data-testid="batch-filter-product" value={filters.product}
                 onChange={(e) => setFilters({ ...filters, product: e.target.value })}
                 placeholder="e.g. Revolax"
                 className="border hairline px-2 py-2 text-sm focus:outline-none focus:border-stone-900 min-w-[180px]" />
        </label>
        <label className="block">
          <span className="mono-label text-[10px] text-stone-500 block mb-1">BATCH NUMBER</span>
          <input data-testid="batch-filter-batch" value={filters.batch}
                 onChange={(e) => setFilters({ ...filters, batch: e.target.value })}
                 placeholder="e.g. ABC12345"
                 className="border hairline px-2 py-2 text-sm focus:outline-none focus:border-stone-900 min-w-[180px]" />
        </label>
        <label className="block">
          <span className="mono-label text-[10px] text-stone-500 block mb-1">FROM</span>
          <input data-testid="batch-filter-from" type="date" value={filters.date_from}
                 onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                 className="border hairline px-2 py-2 text-sm focus:outline-none focus:border-stone-900" />
        </label>
        <label className="block">
          <span className="mono-label text-[10px] text-stone-500 block mb-1">TO</span>
          <input data-testid="batch-filter-to" type="date" value={filters.date_to}
                 onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                 className="border hairline px-2 py-2 text-sm focus:outline-none focus:border-stone-900" />
        </label>
        <button data-testid="batch-reset-btn" onClick={reset}
                className="mono-label text-xs border border-stone-400 text-stone-700 px-3 py-2 hover:bg-stone-100">Reset</button>
      </div>

      {truncated && (
        <p className="text-[11px] text-amber-700">Showing the first 300 matches — narrow the filters to see more.</p>
      )}

      <div className="border hairline bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50">
            <tr className="text-left mono-label text-[11px] text-stone-600">
              <th className="px-4 py-3">DATE</th>
              <th className="px-4 py-3">CLIENT</th>
              <th className="px-4 py-3">TREATMENT</th>
              <th className="px-4 py-3">PRODUCT</th>
              <th className="px-4 py-3">BATCH</th>
              <th className="px-4 py-3">EXPIRY</th>
              <th className="px-4 py-3">QTY</th>
              <th className="px-4 py-3">REF</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={8} className="px-4 py-6 text-stone-500 text-center">Loading…</td></tr>)}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-6 text-stone-500 text-center">No batch records match these filters.</td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={`${r.booking_id}-${r.treatment_item_index}`} className="border-t hairline"
                  data-testid={`batch-row-${r.booking_id}-${r.treatment_item_index}`}>
                <td className="px-4 py-3 whitespace-nowrap">{r.booking_date} · {r.booking_time}</td>
                <td className="px-4 py-3">{r.client_name}<br/><span className="text-xs text-stone-500">{r.client_email}</span></td>
                <td className="px-4 py-3">{r.treatment_name}</td>
                <td className="px-4 py-3">{r.product_name || <span className="text-stone-400">—</span>}</td>
                <td className="px-4 py-3 font-mono">{r.batch_number}</td>
                <td className="px-4 py-3">{r.batch_expiry || <span className="text-stone-400">—</span>}</td>
                <td className="px-4 py-3">{r.quantity_used || <span className="text-stone-400">—</span>}</td>
                <td className="px-4 py-3 font-mono text-xs">{r.booking_reference}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
